import { DateRange } from 'react-day-picker'

import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient'
import { IconType } from '@factorialco/f0-react'
import { OneDataCollection, useDataCollectionSource } from '@factorialco/f0-react/dist/experimental'
import { AnimatePresence } from 'framer-motion'
import { AnalyticsTraceabilityResultsDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import {
  datatableFiltersToJsonLogicFilters,
  filterDefinitionsFromSchema,
} from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import {
  ColumnFormatHint,
  OneRecord,
  formatColumns,
} from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'
import { LoadingBlock } from 'modules/ai_reports/components/LoadingBlock'
import { AiReportsResult } from 'modules/ai_reports/lib/useCreateAnalyticsReportActionResolver'

interface ReportTableAction {
  label: string
  icon: IconType
  onClick: () => void
}

interface ReportTableProps {
  dateFilter?: DateRange
  reportName: string
  sqlQuery: string
  actions?: {
    primary?: ReportTableAction
    secondary?: ReportTableAction[]
  }
  columnFormatHints: ColumnFormatHint[]
  setFilters?: (filters: NonNullable<AiReportsResult['filters']>) => void
  tableId: string
  onUserFacingSqlQueryChange: (sqlQuery: string) => void
}

const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 10

const ReportTable = ({
  sqlQuery,
  actions,
  columnFormatHints,
  setFilters,
  tableId,
  dateFilter,
  onUserFacingSqlQueryChange,
}: ReportTableProps) => {
  const client = useApolloClient()
  const { company, access } = useCurrentSession()

  const startOfToday = moment().startOf('day').toDate()
  const endOfToday = moment().endOf('day').toDate()

  // Used to fetch the columns.
  const { data: columnsData, loading: columnsLoading } = useQuery(
    AnalyticsTraceabilityResultsDocument,
    {
      variables: {
        query: sqlQuery,
        companyId: company.id,
        dateRange: {
          from: (dateFilter?.from ? new Date(dateFilter.from) : startOfToday).toISOString(),
          to: (dateFilter?.to ? new Date(dateFilter.to) : endOfToday).toISOString(),
        },
        page: INITIAL_PAGE,
        perPage: INITIAL_PAGE_SIZE,
        tableId: tableId,
      },
    }
  )

  // FIXME: The Data Collection filters are not updated unless they are added to
  // the Data Collection source dependencies array. If this is fixed, we can
  // remove the hook and use the filters directly.
  const schema = columnsData?.iris?.traceabilityResultsConnection?.nodes?.[0]?.schema
  const filterDefinitions = useMemo(
    () => filterDefinitionsFromSchema(schema ?? []),
    [columnsLoading]
  )

  const source = useDataCollectionSource(
    {
      filters: filterDefinitions,
      totalItemSummary: () => '',
      primaryActions: () => actions?.primary,
      secondaryActions: actions?.secondary
        ? {
            expanded: 1,
            actions: () => actions.secondary || [],
          }
        : undefined,
      dataAdapter: {
        paginationType: 'pages',
        perPage: INITIAL_PAGE_SIZE,
        fetchData: (options) => {
          const pagination = options.pagination
          const perPage = pagination.perPage ?? INITIAL_PAGE_SIZE
          const currentPage = pagination.currentPage ?? INITIAL_PAGE

          const dateFrom = dateFilter?.from ?? startOfToday
          const dateTo = dateFilter?.to ?? endOfToday

          const normalizedFrom = moment(dateFrom).startOf('day').toDate()
          const normalizedTo = moment(dateTo).endOf('day').toDate()

          const filters = options.filters
          const jsonLogicFilters = datatableFiltersToJsonLogicFilters(schema ?? [], filters)

          setFilters?.({ dateRange: { from: normalizedFrom, to: normalizedTo } })

          return client
            .watchQuery({
              query: AnalyticsTraceabilityResultsDocument,
              fetchPolicy: 'cache-and-network',
              variables: {
                query: sqlQuery,
                companyId: company.id,
                dateRange: {
                  from: normalizedFrom.toISOString(),
                  to: normalizedTo.toISOString(),
                },
                page: currentPage,
                perPage: perPage,
                tableId: tableId,
                filters: JSON.stringify(jsonLogicFilters),
              },
              notifyOnNetworkStatusChange: false,
              nextFetchPolicy: 'cache-and-network',
            })
            .map(({ data, loading, error }) => {
              onUserFacingSqlQueryChange(
                data?.iris?.traceabilityResultsConnection?.nodes?.[0]?.sqlQuery ?? ''
              )
              return {
                loading: loading,
                error: error,
                data: {
                  records:
                    data?.iris.traceabilityResultsConnection.nodes.map(
                      (node) =>
                        node.values.reduce<OneRecord>((acc, curr) => {
                          acc[curr.name] = curr.value ?? ''
                          return acc
                        }, {}) ?? []
                    ) ?? [],
                  total: data?.iris.traceabilityResultsConnection.totalCount ?? 0,
                  currentPage: currentPage,
                  pagesCount: Math.ceil(
                    (data?.iris.traceabilityResultsConnection.totalCount ?? 0) / perPage
                  ),
                  perPage: perPage,
                  type: 'pages',
                },
              }
            })
        },
      },
    },
    [sqlQuery, company.id, dateFilter, filterDefinitions]
  )

  return (
    <AnimatePresence mode="wait">
      {columnsLoading ? (
        <LoadingBlock />
      ) : (
        <OneDataCollection
          fullHeight
          source={source}
          visualizations={[
            {
              type: 'table',
              options: {
                frozenColumns: 1,
                columns: formatColumns(
                  columnsData?.iris.traceabilityResultsConnection.nodes || [],
                  columnFormatHints,
                  access.locale
                ),
              },
            },
          ]}
        />
      )}
    </AnimatePresence>
  )
}

export default ReportTable
export { type ReportTableAction, type ReportTableProps }
